/**
 * ShippingSizesAdminPage — super-admin manages the 7-tier shipping size ladder
 * used by vendors in courier / same-day-package / business-logistics categories.
 *
 * Flow:
 *   1. Admin clicks "Generate with AI" to seed 7 sizes from the LLM.
 *   2. Sizes appear in an editable table; admin tweaks dimensions/weights/prices.
 *   3. Admin picks which vendor categories the ladder applies to via chip toggles.
 *   4. Admin clicks Save — persisted to platform_settings.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Package, Sparkles, Save, Loader2, Plus, Trash2, RotateCcw } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const CATEGORY_CHIPS = [
  { slug: 'courier', label: 'Courier services' },
  { slug: 'package', label: 'Same-day package' },
  { slug: 'logistics', label: 'Business logistics' },
];

const BLANK_SIZE = () => ({
  id: `custom-${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  description: '',
  length_cm: 20,
  width_cm: 15,
  height_cm: 10,
  max_weight_kg: 1,
  price_multiplier: 1.0,
  icon: 'Package',
});

const ShippingSizesAdminPage = () => {
  const [sizes, setSizes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Live rate preview so admins see "base × multiplier = estimated fee" per row.
  const [preview, setPreview] = useState(null); // {base_delivery_fee_cad, currency, distance_km}
  const [previewDistance, setPreviewDistance] = useState(5);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/shipping-sizes');
      setSizes(data.sizes || []);
      setCategories(data.categories || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load shipping sizes');
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async (km) => {
    try {
      const { data } = await api.get('/admin/shipping-sizes/rate-preview', {
        params: { distance_km: km, currency: 'CAD' },
      });
      setPreview(data);
    } catch {
      setPreview(null);
    }
  };

  useEffect(() => { load(); loadPreview(previewDistance); }, []);
  useEffect(() => { loadPreview(previewDistance); }, [previewDistance]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/admin/shipping-sizes/generate');
      setSizes(data.sizes || []);
      setDirty(true);
      toast.success('Generated 7 shipping sizes with AI. Review and save.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const updateSize = (idx, patch) => {
    setSizes((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    setDirty(true);
  };

  const removeSize = (idx) => {
    setSizes((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const addSize = () => {
    setSizes((prev) => [...prev, BLANK_SIZE()]);
    setDirty(true);
  };

  const toggleCategory = (slug) => {
    setCategories((prev) => prev.includes(slug) ? prev.filter((c) => c !== slug) : [...prev, slug]);
    setDirty(true);
  };

  const validationError = useMemo(() => {
    if (sizes.length !== 7) return `Need exactly 7 sizes (currently ${sizes.length}).`;
    let prev = 0;
    for (const s of sizes) {
      if (!s.name?.trim()) return 'Every size needs a name.';
      const mult = Number(s.price_multiplier);
      if (!(mult > prev)) return `Price multiplier must strictly increase (${s.name || 'unnamed'} = ${mult}).`;
      prev = mult;
    }
    return null;
  }, [sizes]);

  const save = async () => {
    if (validationError) { toast.error(validationError); return; }
    setSaving(true);
    try {
      const body = {
        sizes: sizes.map((s) => ({
          id: s.id,
          name: s.name,
          description: s.description || '',
          length_cm: Number(s.length_cm),
          width_cm: Number(s.width_cm),
          height_cm: Number(s.height_cm),
          max_weight_kg: Number(s.max_weight_kg),
          price_multiplier: Number(s.price_multiplier),
          icon: s.icon || 'Package',
        })),
        categories,
      };
      const { data } = await api.put('/admin/shipping-sizes', body);
      toast.success(`Saved ${data.count} sizes.`);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="shipping-sizes-admin">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Package className="h-6 w-6 text-emerald-600" />
            <h1 className="text-2xl font-semibold text-slate-900">Shipping sizes</h1>
          </div>
          <p className="text-slate-500 mt-1 text-sm max-w-2xl">
            Define the 7 parcel-size tiers customers see when ordering from courier,
            same-day-package or business-logistics vendors. Prices are calculated as
            <span className="mx-1 font-mono text-slate-800">base_fee × price_multiplier</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="shipping-sizes-reload"
          >
            <RotateCcw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Reload
          </Button>
          <Button
            onClick={generate}
            disabled={generating || saving}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="shipping-sizes-generate"
          >
            {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            Generate with AI
          </Button>
          <Button
            onClick={save}
            disabled={saving || !dirty || !!validationError}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="shipping-sizes-save"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save
          </Button>
        </div>
      </div>

      <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-semibold text-slate-900 mb-1">Rate preview</h2>
            <p className="text-sm text-slate-500">
              Base delivery fee at a chosen distance × each row&apos;s price multiplier. Tune with confidence.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-wide text-slate-500">Distance</label>
            <Input
              type="number"
              min="0"
              step="0.5"
              value={previewDistance}
              onChange={(e) => setPreviewDistance(Math.max(0, Number(e.target.value) || 0))}
              className="h-9 w-24 text-right font-mono"
              data-testid="rate-preview-distance"
            />
            <span className="text-sm text-slate-500">km</span>
          </div>
        </div>
        {preview ? (
          <div className="mt-3 flex flex-wrap items-baseline gap-x-3 text-sm">
            <span className="text-slate-500">Base fee at {preview.distance_km} km:</span>
            <span className="font-mono text-lg font-semibold text-slate-900" data-testid="rate-preview-base">
              CAD {Number(preview.base_delivery_fee_cad || 0).toFixed(2)}
            </span>
            <span className="text-xs text-slate-400">
              ({(preview.breakdown || []).map((c) => `${c.label}: ${Number(c.line_cad).toFixed(2)}`).join(' + ') || 'flat'})
            </span>
          </div>
        ) : (
          <div className="mt-3 text-sm text-slate-400">Base fee unavailable — check platform settings.</div>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
        <h2 className="font-semibold text-slate-900 mb-1">Applies to vendor categories</h2>
        <p className="text-sm text-slate-500 mb-3">
          Only vendors in the ticked categories will offer these sizes at checkout.
        </p>
        <div className="flex flex-wrap gap-2">
          {CATEGORY_CHIPS.map((c) => {
            const active = categories.includes(c.slug);
            return (
              <button
                key={c.slug}
                type="button"
                onClick={() => toggleCategory(c.slug)}
                data-testid={`shipping-sizes-cat-${c.slug}`}
                aria-pressed={active}
                className={`text-sm px-3 py-1.5 rounded-full border transition
                  ${active
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </section>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading sizes…
        </div>
      ) : sizes.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
          <Package className="h-10 w-10 mx-auto text-slate-300" />
          <div className="mt-3 font-semibold text-slate-900">No shipping sizes yet</div>
          <p className="text-sm text-slate-500 mt-1">Click <b>Generate with AI</b> to create the 7-tier ladder.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-right">L cm</th>
                <th className="px-3 py-2 text-right">W cm</th>
                <th className="px-3 py-2 text-right">H cm</th>
                <th className="px-3 py-2 text-right">Max kg</th>
                <th className="px-3 py-2 text-right">Price ×</th>
                <th className="px-3 py-2 text-right">Est. fee</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sizes.map((s, idx) => (
                <tr key={s.id || idx} data-testid={`shipping-size-row-${idx}`}>
                  <td className="px-3 py-2 text-slate-400 font-mono">{idx + 1}</td>
                  <td className="px-3 py-2 min-w-[140px]">
                    <Input
                      value={s.name || ''}
                      onChange={(e) => updateSize(idx, { name: e.target.value })}
                      className="h-9"
                      data-testid={`shipping-size-name-${idx}`}
                    />
                  </td>
                  <td className="px-3 py-2 min-w-[220px]">
                    <Input
                      value={s.description || ''}
                      onChange={(e) => updateSize(idx, { description: e.target.value })}
                      className="h-9"
                    />
                  </td>
                  {['length_cm', 'width_cm', 'height_cm', 'max_weight_kg', 'price_multiplier'].map((k) => (
                    <td key={k} className="px-3 py-2 w-20">
                      <Input
                        type="number"
                        step={k === 'price_multiplier' || k === 'max_weight_kg' ? '0.1' : '1'}
                        min="0"
                        value={s[k]}
                        onChange={(e) => updateSize(idx, { [k]: e.target.value })}
                        className="h-9 text-right font-mono"
                        data-testid={`shipping-size-${k}-${idx}`}
                      />
                    </td>
                  ))}
                  <td className="px-3 py-2 w-28 text-right" data-testid={`shipping-size-estimated-fee-${idx}`}>
                    {preview ? (
                      <div>
                        <div className="font-semibold text-slate-900 font-mono">
                          {(Number(preview.base_delivery_fee_cad || 0) * Number(s.price_multiplier || 0)).toFixed(2)}
                        </div>
                        <div className="text-[10px] text-slate-400 uppercase tracking-wide">CAD</div>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => removeSize(idx)}
                      className="text-slate-400 hover:text-rose-500"
                      aria-label="Delete size"
                      data-testid={`shipping-size-delete-${idx}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-3 border-t border-slate-100 flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={addSize} data-testid="shipping-sizes-add-row">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add row
            </Button>
            {validationError ? (
              <span className="text-xs text-rose-600" data-testid="shipping-sizes-validation-error">
                {validationError}
              </span>
            ) : (
              dirty && <span className="text-xs text-amber-600">Unsaved changes</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingSizesAdminPage;
